/** Products list — DataTable с фильтрами, bulk actions и CSV import/export. */

import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Download, Plus, Search, Upload, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DataTable } from "@/components/shared/DataTable";
import { BulkActionsBar } from "@/components/products/BulkActionsBar";
import { CsvImportDialog } from "@/components/products/CsvImportDialog";
import { ProductExportDialog } from "@/components/products/ExportDialog";
import { PrimaryCategoryPicker } from "@/components/products/CategoryPicker";
import { InlineCategoryCell } from "@/components/products/InlineCategoryCell";
import { api } from "@/lib/api";
import { formatPrice } from "@/lib/utils";

interface AdminProductRow {
  id: number;
  sku: string;
  name: string;
  category_id: number;
  base_price: number;
  sale_price: number | null;
  stock_quantity: number;
  is_active: boolean;
  rating_avg: number;
  reviews_count: number;
  image: { thumb_url: string } | null;
}

interface AdminCategoryRaw {
  id: number;
  parent_id: number | null;
  slug: string;
  translations: Array<{ language: string; name: string }>;
}

function categoryName(c: AdminCategoryRaw | undefined): string {
  if (!c) return "";
  const ru = c.translations.find((t) => t.language === "ru")?.name;
  return ru ?? c.translations[0]?.name ?? c.slug;
}

const PAGE_SIZE_KEY = "admin-products-page-size";

export function ProductsListPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(() => {
    const saved = localStorage.getItem(PAGE_SIZE_KEY);
    const n = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(n) && [25, 50, 100].includes(n) ? n : 25;
  });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "draft">("all");
  const [categoryFilter, setCategoryFilter] = useState<number | null>(() => {
    const c = searchParams.get("category_id");
    return c ? parseInt(c, 10) : null;
  });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  // Persist выбранный pageSize в localStorage чтобы не сбрасывался
  useEffect(() => {
    localStorage.setItem(PAGE_SIZE_KEY, String(perPage));
  }, [perPage]);

  // Sync category filter to URL так чтобы можно было поделиться ссылкой
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (categoryFilter != null) next.set("category_id", String(categoryFilter));
    else next.delete("category_id");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter]);

  const { data, isLoading } = useQuery({
    queryKey: [
      "admin-products",
      page,
      perPage,
      q,
      statusFilter,
      categoryFilter,
    ],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        page,
        per_page: perPage,
      };
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      if (categoryFilter != null) params.category_id = categoryFilter;
      const { data } = await api.get("/products", { params });
      return data as { data: AdminProductRow[]; pagination: { total: number } };
    },
  });

  // Грузим категории — для рендеринга колонки + filter dropdown в одном месте
  const { data: categories } = useQuery({
    queryKey: ["admin-categories-raw"],
    queryFn: async () => {
      const { data } = await api.get<AdminCategoryRaw[]>("/categories");
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const categoryById = useMemo(() => {
    const m = new Map<number, AdminCategoryRaw>();
    for (const c of categories ?? []) m.set(c.id, c);
    return m;
  }, [categories]);

  const visibleIds = useMemo(
    () => (data?.data ?? []).map((p) => p.id),
    [data?.data],
  );
  const allChecked =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someChecked =
    !allChecked && visibleIds.some((id) => selectedIds.has(id));

  const toggleAll = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const id of visibleIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };
  const toggleOne = (id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const downloadCsv = async () => {
    try {
      const url = `${import.meta.env.VITE_API_URL}/products/csv-export?lang=ru`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `products-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e: any) {
      toast.error(`Ошибка экспорта: ${e?.message ?? "unknown"}`);
    }
  };

  const columns: ColumnDef<AdminProductRow>[] = [
    {
      id: "select",
      header: () => (
        <Checkbox
          checked={allChecked}
          indeterminate={someChecked}
          onChange={(e) => toggleAll(e.target.checked)}
          aria-label="Выбрать все на странице"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={selectedIds.has(row.original.id)}
          onChange={(e) => toggleOne(row.original.id, e.target.checked)}
          aria-label={`Выбрать ${row.original.sku}`}
        />
      ),
    },
    {
      id: "image",
      header: "",
      cell: ({ row }) =>
        row.original.image ? (
          <img
            src={row.original.image.thumb_url}
            alt=""
            className="w-10 h-10 object-cover rounded"
          />
        ) : (
          <div className="w-10 h-10 bg-muted rounded" />
        ),
    },
    {
      accessorKey: "name",
      header: "Название",
      cell: ({ row }) => (
        <Link
          to={`/products/${row.original.id}/edit`}
          className="hover:underline font-medium"
        >
          {row.original.name}
        </Link>
      ),
    },
    {
      id: "category",
      header: "Категория",
      cell: ({ row }) => {
        const c = categoryById.get(row.original.category_id);
        const name = categoryName(c) || `id ${row.original.category_id}`;
        return (
          <InlineCategoryCell
            productId={row.original.id}
            categoryId={row.original.category_id}
            categoryName={name}
          />
        );
      },
    },
    { accessorKey: "sku", header: "SKU" },
    {
      accessorKey: "base_price",
      header: "Цена",
      cell: ({ row }) => (
        <div>
          {row.original.sale_price != null && (
            <div className="font-semibold text-destructive">
              {formatPrice(row.original.sale_price)}
            </div>
          )}
          <div
            className={
              row.original.sale_price != null
                ? "text-xs text-muted-foreground line-through"
                : ""
            }
          >
            {formatPrice(row.original.base_price)}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "stock_quantity",
      header: "Остаток",
      cell: ({ row }) => (
        <span
          className={
            row.original.stock_quantity === 0
              ? "text-destructive font-medium"
              : ""
          }
        >
          {row.original.stock_quantity}
        </span>
      ),
    },
    {
      accessorKey: "rating_avg",
      header: "Рейтинг",
      cell: ({ row }) =>
        row.original.reviews_count > 0
          ? `${row.original.rating_avg.toFixed(1)} (${row.original.reviews_count})`
          : "—",
    },
    {
      accessorKey: "is_active",
      header: "Статус",
      cell: ({ row }) =>
        row.original.is_active ? (
          <Badge variant="success">Active</Badge>
        ) : (
          <Badge variant="secondary">Draft</Badge>
        ),
    },
  ];

  const activeCategoryName = categoryFilter
    ? categoryName(categoryById.get(categoryFilter))
    : null;

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading">{t("nav.products")}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadCsv}>
            <Download className="w-4 h-4 mr-1" />
            Экспорт CSV
          </Button>
          <Button variant="outline" onClick={() => setExportOpen(true)}>
            <Download className="w-4 h-4 mr-1" />
            Экспорт (поля)
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <Upload className="w-4 h-4 mr-1" />
            Импорт CSV
          </Button>
          <Button onClick={() => navigate("/products/new")}>
            <Plus className="w-4 h-4 mr-1" />
            {t("common.create")}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Поиск по name или SKU"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>

        <Select
          value={statusFilter}
          onValueChange={(v: any) => {
            setStatusFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>

        <div className="w-72">
          <PrimaryCategoryPicker
            value={categoryFilter}
            onChange={(id) => {
              setCategoryFilter(id);
              setPage(1);
            }}
          />
        </div>

        {categoryFilter != null && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setCategoryFilter(null);
              setPage(1);
            }}
            className="text-xs"
          >
            <X className="mr-1 h-3 w-3" />
            {activeCategoryName ? `Сбросить «${activeCategoryName}»` : "Сбросить категорию"}
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        total={data?.pagination?.total ?? 0}
        page={page}
        pageSize={perPage}
        isLoading={isLoading}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPerPage(n);
          setPage(1);
        }}
      />

      <BulkActionsBar
        selectedIds={Array.from(selectedIds)}
        onClear={() => setSelectedIds(new Set())}
      />

      <CsvImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />

      <ProductExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
      />
    </div>
  );
}
